import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Invitation, Product } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Button, Callout, EmptyState, ListRow } from '@beyond-js/ui/react';
import { Outage, Panel, Problem, type Unread, useUnread } from '@accounts/ui';

/** Where a product opens, in the organization just joined (ARR-06): its entry, else its first origin */
export function opening(product: Product | undefined, organization: string): string | undefined {
	const base = product?.entry ?? product?.origins[0];
	if (!base) return undefined;
	const url = new URL(base);
	url.searchParams.set('organization', organization);
	return url.toString();
}

/**
 * The invitations waiting for the signed-in person (ARR-03): who invited them, to what and as what,
 * with Accept and Decline. Accepting says where they joined and offers the product they were invited
 * from, in that organization, and the organization itself (ARR-06).
 *
 * @param always Whether to show the region when nothing is waiting (the invitations page, where its
 * heading is drawn at once and its first read waits in it) or not (the account home)
 * @param onRead Called when the read made on opening (or after a renewed session) has ended, whatever it
 * got: the account home draws what follows the invitations only then, so they never arrive above it (D185)
 *
 * When Accounts does not answer the read (D163), the account home's notice says so once and the region
 * says it in its place; on its own page the notice is the page's, its body while nothing was read. Over
 * invitations kept from the last read it says when that was. An answer keeps its own outcome.
 */
export function Invitations({ always, onRead }: { always: boolean; onRead?: () => void }) {
	const { text, date, organizations, accounts, refresh, renewed, language } = usePresence();
	const told = useRef(onRead);
	told.current = onRead;
	const [invitations, setInvitations] = useState<Invitation[]>();
	const [products, setProducts] = useState<Product[]>([]);
	// When they were last read, and why they could not be read since; a failed answer is `failure`
	const [at, setAt] = useState<number>();
	const [unread, setUnread] = useState<Refusal>();
	const [failure, setFailure] = useState<unknown>();
	const [busy, setBusy] = useState<string>();
	const [joined, setJoined] = useState<{ organization: string; name: string; product?: Product }>();
	const [notice, setNotice] = useState<string>();

	// Whether Accounts answered, which takes the read out of the page's outage
	const read = useCallback(
		() =>
			Refusal.answered(async () => {
				setInvitations(await organizations.received());
				setAt(Date.now());
			}, setUnread),
		[organizations]
	);
	const item: Unread = { key: 'invitations', title: text('received.title'), unread, run: read };
	// Nothing waiting is said only on its own page; not knowing is said everywhere
	const drawn = always || Boolean(invitations?.length || joined || failure || (unread && !invitations));
	// On the account home it joins the home's notice while it is drawn; on its own page it is the page's notice, below
	const outage = useUnread(item, !always && drawn);

	useEffect(() => {
		void read().then(() => told.current?.());
		accounts.providers().then(found => setProducts(found.products), () => undefined);
	}, [read, accounts, renewed]);

	async function answer(invitation: Invitation, accept: boolean) {
		setBusy(invitation.id);
		setFailure(undefined);
		setNotice(undefined);
		try {
			if (accept) {
				const taken = await organizations.take(invitation.id);
				setJoined({ organization: taken.organization, name: invitation.name ?? taken.organization, product: products.find(product => product.id === taken.product) });
				await refresh();
			} else {
				await organizations.decline(invitation.id);
				setNotice(text('received.declined'));
			}
			await read();
		} catch (error) {
			if (!(Refusal.is(error) && error.quiet)) setFailure(error);
			// Nothing came back, so the answer may have been taken: what is shown is read again
			if (Refusal.is(error) && error.unknown) await Promise.all([read(), refresh()]);
		} finally {
			setBusy(undefined);
		}
	}

	if (!drawn) return null;
	const open = joined ? opening(joined.product, joined.organization) : undefined;
	const whole = always && outage && !invitations;
	const region = (
		<Panel
			id="invitations"
			title={text('received.title')}
			waiting={invitations === undefined && !unread && !joined}
			unavailable={outage && !invitations}
			updated={outage && invitations ? (at ?? null) : undefined}
		>
			{failure ? <Problem error={failure} check={read} /> : null}
			{unread && !outage ? <Problem error={unread} /> : null}
			{notice ? <Callout tone="success" live title={notice} /> : null}
			{joined ? (
				<Callout
					tone="success"
					live
					title={text('received.joined', { organization: joined.name })}
					actions={
						<>
							{open && joined.product ? <Button variant="primary" href={open} label={text('received.open', { product: joined.product.name })} /> : null}
							<Link className="underline" to={`/organizations/${encodeURIComponent(joined.organization)}`}>
								{text('received.go', { organization: joined.name })}
							</Link>
						</>
					}
				/>
			) : null}
			{invitations && !invitations.length && always ? <EmptyState title={text('received.none')} frame={false} labels={EmptyState.labels[language]} /> : null}
			{invitations?.length ? (
				<ul className="bui-rows" data-framed aria-label={text('received.title')}>
					{invitations.map(invitation => (
						<ListRow
							key={invitation.id}
							title={text('received.item', { inviter: invitation.inviter ?? text('received.someone'), organization: invitation.name ?? '' })}
							meta={[
								text('received.as', { role: text(`role.${invitation.role}`), date: date(invitation.expires) }),
								invitation.product ? text('received.from', { product: products.find(product => product.id === invitation.product)?.name ?? invitation.product }) : null
							]}
							lead={busy === invitation.id ? { state: 'busy' } : null}
							actions={
								busy === invitation.id
									? []
									: [
											{ label: text('received.accept'), onSelect: () => void answer(invitation, true) },
											{ label: text('received.decline'), onSelect: () => void answer(invitation, false) }
										]
							}
						/>
					))}
				</ul>
			) : null}
		</Panel>
	);
	if (!always) return region;
	return (
		<>
			<Outage whole={whole} reads={[item]} />
			{whole ? null : region}
		</>
	);
}

/** The invitations on the account home: nothing when none is waiting; `onRead` once their read answered */
export function PendingInvitations({ onRead }: { onRead?: () => void }) {
	return <Invitations always={false} onRead={onRead} />;
}
